import { html, nothing } from "lit";
import { formatDurationMs } from "../format.ts";
import { icons } from "../icons.ts";
import type {
  FleetUnitResultArtifact,
  FleetValidationEvidenceItem,
  FleetWorkPlanRecord,
  FleetWorkUnit,
} from "../types.ts";
import { fleetStatusPillClass } from "./fleet-plan-labels.ts";
import {
  inferFleetDisplayRole,
  roleOrderIndex,
  type FleetDisplayRole,
} from "./fleet-plan-roles.ts";
import { mergeSessionLogsForUnits, type FleetSessionLogLine } from "./fleet-plan-session-logs.ts";

export type AgentCardStatus =
  | "completed"
  | "running"
  | "waiting"
  | "failed"
  | "blocked"
  | "stopped";

export type AggregatedFile = {
  path: string;
  added: number;
  removed: number;
  changeKind: "A" | "M" | "D";
};

export type AgentCardState = {
  role: FleetDisplayRole;
  agentId: string;
  units: FleetWorkUnit[];
  done: number;
  total: number;
  running: number;
  failed: number;
  status: AgentCardStatus;
  durationMs?: number;
  files: AggregatedFile[];
  evidence: FleetValidationEvidenceItem[];
};

function formatDiffStat(added?: number, removed?: number): string {
  const a = typeof added === "number" ? `+${added}` : "";
  const r = typeof removed === "number" ? `-${removed}` : "";
  if (a && r) {
    return `${a} ${r}`;
  }
  return a || r || "";
}

function evidenceStatusClass(status: FleetValidationEvidenceItem["status"]): string {
  switch (status) {
    case "passed":
      return "pill--ok";
    case "failed":
      return "pill--danger";
    case "running":
      return "pill--running";
    case "skipped":
      return "pill--muted";
    default:
      return "pill--warn";
  }
}

function evidenceKindLabel(kind: FleetValidationEvidenceItem["kind"]): string {
  switch (kind) {
    case "unit_tests":
      return "Unit tests";
    case "type_check":
      return "Type check";
    case "lint_check":
      return "Lint";
    case "api_endpoints":
      return "API endpoints";
    case "integration_tests":
      return "Integration tests";
    default:
      return kind;
  }
}

export function changeKindForArtifact(artifact: FleetUnitResultArtifact): "A" | "M" | "D" {
  const added = artifact.added ?? 0;
  const removed = artifact.removed ?? 0;
  if (added > 0 && removed === 0) {
    return "A";
  }
  if (added === 0 && removed > 0) {
    return "D";
  }
  return "M";
}

export function aggregateChangedFiles(units: readonly FleetWorkUnit[]): AggregatedFile[] {
  const byPath = new Map<string, AggregatedFile>();
  for (const unit of units) {
    for (const artifact of unit.result?.artifacts ?? []) {
      const existing = byPath.get(artifact.path);
      const added = artifact.added ?? 0;
      const removed = artifact.removed ?? 0;
      if (existing) {
        existing.added += added;
        existing.removed += removed;
        existing.changeKind = changeKindForArtifact({
          path: artifact.path,
          added: existing.added,
          removed: existing.removed,
        });
      } else {
        byPath.set(artifact.path, {
          path: artifact.path,
          added,
          removed,
          changeKind: changeKindForArtifact(artifact),
        });
      }
    }
  }
  return [...byPath.values()].sort((a, b) => a.path.localeCompare(b.path));
}

const EVIDENCE_RANK: Record<FleetValidationEvidenceItem["status"], number> = {
  failed: 4,
  running: 3,
  pending: 2,
  passed: 1,
  skipped: 0,
};

export function aggregateValidationEvidence(
  units: readonly FleetWorkUnit[],
): FleetValidationEvidenceItem[] {
  const byKind = new Map<string, FleetValidationEvidenceItem>();
  for (const unit of units) {
    for (const item of unit.result?.validationEvidence ?? []) {
      const current = byKind.get(item.kind);
      if (!current || EVIDENCE_RANK[item.status] >= EVIDENCE_RANK[current.status]) {
        byKind.set(item.kind, {
          ...item,
          durationMs: (current?.durationMs ?? 0) + (item.durationMs ?? 0) || item.durationMs,
        });
      }
    }
  }
  return [...byKind.values()];
}

export function agentCardStatus(units: readonly FleetWorkUnit[]): AgentCardStatus {
  // BIBLE dashboard: a blocked unit is awaiting operator decision, not a failure.
  // Surface it as "blocked" so the card does not look like a failed role.
  if (units.some((u) => u.status === "blocked")) {
    return "blocked";
  }
  if (units.some((u) => u.status === "failed")) {
    return "failed";
  }
  if (units.some((u) => u.status === "running" || u.status === "in_review")) {
    return "running";
  }
  if (units.some((u) => u.status === "stopped" || u.status === "paused")) {
    return "stopped";
  }
  if (units.length > 0 && units.every((u) => u.status === "done" || u.status === "cancelled")) {
    return "completed";
  }
  return "waiting";
}

export function agentDurationMs(
  units: readonly FleetWorkUnit[],
  nowMs = Date.now(),
): number | undefined {
  const starts = units
    .map((u) => u.spawnedAtMs ?? u.firstEventAtMs)
    .filter((ms): ms is number => typeof ms === "number");
  if (starts.length === 0) {
    const completed = units
      .map((u) => u.result?.completedAtMs)
      .filter((ms): ms is number => typeof ms === "number");
    if (completed.length === 0) {
      return undefined;
    }
  }
  const ends = units.map((u) => {
    if (u.status === "running" || u.status === "in_review") {
      return nowMs;
    }
    return u.result?.completedAtMs ?? u.lastEventAtMs ?? u.failedAtMs ?? nowMs;
  });
  const start = starts.length > 0 ? Math.min(...starts) : Math.min(...ends);
  return Math.max(0, Math.max(...ends) - start);
}

function formatClock(ms?: number): string {
  if (!ms) {
    return "--:--:--";
  }
  return new Date(ms).toLocaleTimeString(undefined, { hour12: false });
}

function collectOutputLines(
  units: readonly FleetWorkUnit[],
  logsBySession?: Record<string, FleetSessionLogLine[]>,
): Array<{ atMs?: number; text: string }> {
  const fromSessions = mergeSessionLogsForUnits(units, logsBySession);
  if (fromSessions.length > 0) {
    return fromSessions;
  }
  const lines: Array<{ atMs?: number; text: string }> = [];
  for (const unit of units) {
    const summary = unit.result?.summary?.trim();
    if (summary) {
      for (const line of summary.split(/\n+/)) {
        const text = line.trim();
        if (text) {
          lines.push({ atMs: unit.result?.completedAtMs ?? unit.lastEventAtMs, text });
        }
      }
      continue;
    }
    if (unit.status === "running") {
      lines.push({
        atMs: unit.lastEventAtMs,
        text: `${unit.title} is running…`,
      });
    } else if (unit.lastEventAtMs) {
      lines.push({
        atMs: unit.lastEventAtMs,
        text: `${unit.title}: last event`,
      });
    }
  }
  return lines;
}

export function buildAgentCards(plan: FleetWorkPlanRecord, nowMs = Date.now()): AgentCardState[] {
  const byRole = new Map<string, FleetWorkUnit[]>();
  const roleFor = new Map<string, FleetDisplayRole>();
  for (const unit of plan.units) {
    const role = inferFleetDisplayRole({
      ...unit,
      plannerAgentId: plan.plannerAgentId,
    });
    const list = byRole.get(role.roleId) ?? [];
    list.push(unit);
    byRole.set(role.roleId, list);
    roleFor.set(role.roleId, role);
  }

  const cards: AgentCardState[] = [];
  for (const [roleId, units] of byRole) {
    const role = roleFor.get(roleId)!;
    cards.push({
      role,
      agentId: units[0]?.targetAgentId?.trim() || plan.plannerAgentId,
      units,
      done: units.filter((u) => u.status === "done").length,
      total: units.length,
      running: units.filter((u) => u.status === "running").length,
      failed: units.filter((u) => u.status === "failed").length,
      status: agentCardStatus(units),
      durationMs: agentDurationMs(units, nowMs),
      files: aggregateChangedFiles(units),
      evidence: aggregateValidationEvidence(units),
    });
  }
  return [...cards].sort((a, b) => {
    const order = roleOrderIndex(a.role.roleId) - roleOrderIndex(b.role.roleId);
    if (order !== 0) {
      return order;
    }
    return b.total - a.total;
  });
}

function statusLabel(status: AgentCardStatus): string {
  switch (status) {
    case "completed":
      return "Completed";
    case "running":
      return "Running";
    case "waiting":
      return "Waiting";
    case "failed":
      return "Failed";
    case "blocked":
      return "Blocked";
    case "stopped":
      return "Stopped";
    default:
      return status;
  }
}

export function renderFleetPlanAgentCards(params: {
  plan: FleetWorkPlanRecord;
  expandedRoleIds?: Set<string>;
  onToggleExpandedRole?: (roleId: string) => void;
  nowMs?: number;
  sessionLogs?: Record<string, FleetSessionLogLine[]>;
}) {
  const cards = buildAgentCards(params.plan, params.nowMs);
  if (cards.length === 0) {
    return nothing;
  }

  return html`
    <div class="fleet-agent-cards" data-testid="fleet-agent-cards">
      <div class="fleet-agent-cards__title">Fleet execution</div>
      ${cards.map((card) =>
        renderAgentCard(card, {
          open: params.expandedRoleIds?.has(card.role.roleId) ?? false,
          onToggle: params.onToggleExpandedRole,
          sessionLogs: params.sessionLogs,
        }),
      )}
    </div>
  `;
}

function renderAgentCard(
  card: AgentCardState,
  options: {
    open: boolean;
    onToggle?: (roleId: string) => void;
    sessionLogs?: Record<string, FleetSessionLogLine[]>;
  },
) {
  const progress = card.total > 0 ? Math.round((card.done / card.total) * 100) : 0;
  const live = card.status === "running";
  const output = collectOutputLines(card.units, options.sessionLogs);
  return html`
    <details
      class="fleet-agent-card ${live ? "is-running" : ""}"
      data-agent-id=${card.agentId}
      data-role-id=${card.role.roleId}
      ?open=${options.open}
    >
      <summary
        class="fleet-agent-card__header"
        @click=${(e: Event) => {
          e.preventDefault();
          options.onToggle?.(card.role.roleId);
        }}
      >
        <span class="fleet-agent-card__avatar" data-role=${card.role.roleId} aria-hidden="true">
          ${card.role.roleName.slice(0, 1)}
        </span>
        <div class="fleet-agent-card__identity">
          <span class="fleet-agent-card__name">${card.role.roleName}</span>
          <span class="fleet-agent-card__purpose">${card.role.purpose}</span>
        </div>
        <span class=${fleetStatusPillClass(card.status)}>${statusLabel(card.status)}</span>
        <div class="fleet-agent-card__progress-wrap">
          <div class="fleet-agent-card__progress">
            <div
              class="fleet-agent-card__progress-bar ${live ? "is-running" : ""}"
              style="width: ${progress}%"
              role="progressbar"
              aria-valuenow=${progress}
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>
          <span class="fleet-agent-card__count">${card.done}/${card.total}</span>
        </div>
        <span class="fleet-agent-card__duration">
          ${icons.clock} ${card.durationMs !== undefined ? formatDurationMs(card.durationMs) : "—"}
        </span>
        <span class="btn btn--sm fleet-agent-card__view">View</span>
      </summary>

      <div class="fleet-agent-card__detail fleet-agent-card__columns">
        <section class="fleet-agent-card__section">
          <div class="fleet-agent-card__section-title">
            Implementation output
            ${live ? html`<span class="pill pill--sm pill--running">Live logs</span>` : nothing}
          </div>
          ${
            output.length === 0
              ? html`<div class="muted fleet-agent-card__log-empty">
                  Waiting for child-session output.
                </div>`
              : html`
                  <ol class="fleet-agent-card__log">
                    ${output.map(
                      (line) => html`
                        <li>
                          <span class="mono muted">${formatClock(line.atMs)}</span>
                          <span>${line.text}</span>
                        </li>
                      `,
                    )}
                  </ol>
                `
          }
        </section>
        <section class="fleet-agent-card__section">
          <div class="fleet-agent-card__section-title">
            Changed files${card.files.length > 0 ? ` (${card.files.length})` : ""}
          </div>
          ${
            card.files.length === 0
              ? html`<div class="muted">No file changes yet.</div>`
              : html`
                  <ul class="fleet-agent-card__file-list">
                    ${card.files.map(
                      (file) => html`
                        <li>
                          ${icons.fileText}
                          <span class="mono fleet-agent-card__file-path" title=${file.path}
                            >${file.path}</span
                          >
                          <span class="fleet-agent-card__file-kind" data-kind=${file.changeKind}
                            >${file.changeKind}</span
                          >
                          <span class="fleet-agent-card__diff-stat"
                            >${formatDiffStat(file.added, file.removed)}</span
                          >
                        </li>
                      `,
                    )}
                  </ul>
                `
          }
        </section>
        <section class="fleet-agent-card__section">
          <div class="fleet-agent-card__section-title">Validation evidence</div>
          ${
            card.evidence.length === 0
              ? html`<div class="muted">No evidence yet.</div>`
              : html`
                  <div class="fleet-agent-card__evidence">
                    ${card.evidence.map(
                      (e) => html`
                        <div class="fleet-agent-card__evidence-item">
                          <span class="pill pill--sm ${evidenceStatusClass(e.status)}"
                            >${e.status}</span
                          >
                          <span class="fleet-agent-card__evidence-kind"
                            >${evidenceKindLabel(e.kind)}</span
                          >
                          <span class="muted">${e.detail ?? e.status}</span>
                          ${
                            e.durationMs
                              ? html`<span class="muted mono"
                                  >${formatDurationMs(e.durationMs)}</span
                                >`
                              : nothing
                          }
                        </div>
                      `,
                    )}
                  </div>
                `
          }
        </section>
      </div>
    </details>
  `;
}

export function collectPlanChangedFiles(plan: FleetWorkPlanRecord): AggregatedFile[] {
  return aggregateChangedFiles(plan.units);
}

export function collectPlanEvidence(plan: FleetWorkPlanRecord): FleetValidationEvidenceItem[] {
  return aggregateValidationEvidence(plan.units);
}
